// ─────────────────────────────────────────────────────────────────────────────
// SEO & AI SEARCH — THE SINGLE SOURCE OF TRUTH
//
// This file is what the "Settings → SEO & AI Search" page reads and writes.
// Both halves of the project share it: the client renders its meta tags from
// here, and the Worker generates robots.txt / sitemap.xml / llms.txt from here
// too. Fix it in one place, it is right everywhere.
//
// NOT THE PLACE FOR UI TEXT. Anything a visitor reads as interface copy lives
// in src/i18n/ and is translated per language. What lives here is the website's
// IDENTITY — its name and its own description — which is one piece of content,
// not a translated label.
// ─────────────────────────────────────────────────────────────────────────────

export interface SeoConfig {
  /** The brand name. Goes into <title>, og:site_name and JSON-LD. */
  siteName: string
  /** A one or two sentence description. This is the line AI quotes for the site. */
  description: string
  /** The root URL with NO trailing slash. Left empty, it is taken from the request. */
  siteUrl: string
  /**
   * The website's own language, as `xx_XX` — it becomes og:locale and JSON-LD
   * inLanguage. This is the language of the CONTENT, which is not the same
   * thing as the interface language a visitor picks in src/i18n/.
   */
  locale: string
  /** The social share image (1200×630). An absolute URL or /og.png. */
  ogImage: string
  /** The entity type for schema.org JSON-LD. */
  organizationType: 'Organization' | 'LocalBusiness' | 'Person' | 'WebSite'
  /** Main topics — used for llms.txt, NOT for <meta keywords> (long dead). */
  keywords: string[]
  /** Social links → JSON-LD sameAs, which helps AI tie the site to the brand. */
  sameAs: string[]

  /**
   * Allow AI providers' crawlers to read the website.
   *
   * ON (the default) = the website has a chance of being cited by ChatGPT,
   * Claude, Perplexity, Gemini and the rest when someone asks. That is the new
   * marketing channel.
   * OFF = robots.txt blocks those bots outright.
   */
  allowAiCrawlers: boolean

  /**
   * Serve prerendered HTML to crawlers.
   *
   * WHY THIS IS CRITICAL: this is a SPA — a real browser runs JavaScript and
   * then sees the content, but most AI crawlers do NOT run JavaScript. Without
   * this flag, what GPTBot and ClaudeBot see is an empty
   * <div id="root"></div>, and the website is effectively invisible to AI
   * search. The Worker detects the bot and injects the real content into the
   * HTML instead.
   */
  prerenderForBots: boolean

  /** Static pages that belong in the sitemap, beyond the database's content. */
  staticRoutes: string[]
}

export const seo: SeoConfig = {
  // These two lines are the first thing anyone sees: they go into <title>, into
  // the large heading on the home page, into og:site_name and into JSON-LD.
  // Renaming the project means editing them HERE, in one place.
  siteName: 'Ốp Lab',
  description:
    'Phụ kiện điện thoại làm tại Việt Nam: ốp lưng da thật, sạc GaN 65W, kính cường lực 9H và giá đỡ nhôm — bảo hành tới 24 tháng, dán kính miễn phí, giao trong 2 giờ ở Hà Nội và TP.HCM.',
  siteUrl: '',
  locale: 'vi_VN',
  ogImage: '/og.png',
  organizationType: 'LocalBusiness',
  keywords: [
    'ốp lưng da',
    'phụ kiện điện thoại',
    'sạc GaN 65W',
    'kính cường lực 9H',
    'giá đỡ điện thoại nhôm',
    'cáp sạc 240W',
    'phone accessories Vietnam',
  ],
  sameAs: [
    'https://www.facebook.com/oplab.vn',
    'https://www.instagram.com/oplab.vn',
    'https://www.tiktok.com/@oplab.vn',
  ],
  allowAiCrawlers: true,
  prerenderForBots: true,
  // Only pages that SHOULD be indexed. /login, /register and /account render
  // <meta name="robots" content="noindex">, so listing them here would ask
  // search engines to crawl pages that then tell them to go away.
  staticRoutes: ['/', '/shop', '/contact']
}

/** Builds a per-page title: "Account — Upbo". */
export function pageTitle(title?: string): string {
  return title && title !== seo.siteName ? `${title} — ${seo.siteName}` : seo.siteName
}

/** An absolute URL, preferring the configured siteUrl over the request origin. */
export function absoluteUrl(path: string, origin?: string): string {
  const base = (seo.siteUrl || origin || '').replace(/\/$/, '')
  return `${base}${path.startsWith('/') ? path : `/${path}`}`
}
